import type {
  PersistedClient,
  Persister,
} from '@tanstack/query-persist-client-core'

interface PendingWrite {
  client: PersistedClient
  settled: Array<() => void>
}

export interface DisposablePersister extends Persister {
  /** Fence queued writes synchronously, then wait for any storage write already started. */
  dispose(): Promise<void>
}

/**
 * Rewriting an unchanged cache still costs a full structured clone on the main
 * thread. Refetches that return equal data keep the same `data` reference
 * (structural sharing), so identity is a cheap, exact change check. The blob
 * timestamp still drives restore freshness, so unchanged content is rewritten
 * at most once per heartbeat.
 */
export const UNCHANGED_PERSIST_HEARTBEAT_MS = 60_000

const identities = new WeakMap<object, number>()
let nextIdentity = 0
function identity(value: unknown): string {
  if (
    value === null ||
    (typeof value !== 'object' && typeof value !== 'function')
  )
    return `${typeof value}:${String(value)}`
  let id = identities.get(value)
  if (id === undefined) {
    id = ++nextIdentity
    identities.set(value, id)
  }
  return `#${id}`
}

/** Changes whenever any persisted query or paused write would differ, apart
 * from fetch timestamps. */
export function persistedContentSignature(client: PersistedClient): string {
  const parts = [client.buster]
  for (const query of client.clientState.queries)
    parts.push(
      query.queryHash,
      query.state.status,
      identity(query.state.data),
      identity(query.meta),
    )
  parts.push('|')
  for (const mutation of client.clientState.mutations)
    parts.push(identity(mutation.state), identity(mutation.meta))
  return parts.join('\u0000')
}

/**
 * Serialize expensive full-cache writes and retain only the newest trailing
 * snapshot while a write or throttle window is active.
 */
export function createSerializedThrottledPersister(
  inner: Omit<Persister, 'persistClient'> & {
    /** `false` reports a declined write, so the same content is retried. */
    persistClient: (client: PersistedClient) => Promise<boolean | void> | void
  },
  throttleMs = 1500,
): DisposablePersister {
  let pendingWrite: PendingWrite | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let inFlight: Promise<void> | null = null
  let removalPromise: Promise<void> | null = null
  let lastWriteStartedAt = 0
  let lastWritten: { signature: string; at: number } | null = null
  let disposed = false

  const settlePendingWrite = () => {
    if (!pendingWrite) return
    pendingWrite.settled.forEach(settle => settle())
    pendingWrite = null
  }

  const schedule = () => {
    if (disposed) {
      settlePendingWrite()
      return
    }
    if (!pendingWrite || timer || inFlight || removalPromise) return

    const elapsed = Date.now() - lastWriteStartedAt
    const delay =
      lastWriteStartedAt === 0 ? 0 : Math.max(0, throttleMs - elapsed)

    if (delay > 0) {
      timer = setTimeout(() => {
        timer = null
        schedule()
      }, delay)
      return
    }

    const write = pendingWrite
    pendingWrite = null
    const signature = persistedContentSignature(write.client)
    if (
      lastWritten?.signature === signature &&
      Date.now() - lastWritten.at < UNCHANGED_PERSIST_HEARTBEAT_MS
    ) {
      write.settled.forEach(settle => settle())
      return
    }
    const startedAt = Date.now()
    lastWriteStartedAt = startedAt
    lastWritten = null

    inFlight = Promise.resolve()
      .then(() => inner.persistClient(write.client))
      .then(written => {
        if (written !== false) lastWritten = { signature, at: startedAt }
      })
      .catch(() => {
        console.warn('[QueryCache] Failed to persist throttled client')
      })
      .finally(() => {
        write.settled.forEach(settle => settle())
        inFlight = null
        schedule()
      })
  }

  return {
    persistClient: (client: PersistedClient) =>
      new Promise<void>(resolve => {
        if (disposed) {
          resolve()
          return
        }
        if (pendingWrite) {
          pendingWrite.client = client
          pendingWrite.settled.push(resolve)
        } else {
          pendingWrite = { client, settled: [resolve] }
        }
        schedule()
      }),
    restoreClient: async () => {
      if (disposed) return undefined
      if (removalPromise) await removalPromise
      if (inFlight) await inFlight
      if (disposed) return undefined
      const restored = await inner.restoreClient()
      return disposed ? undefined : restored
    },
    dispose: async () => {
      disposed = true
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      settlePendingWrite()
      if (inFlight) await inFlight
    },
    removeClient: () => {
      if (removalPromise) return removalPromise

      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      // A queued snapshot predates the removal and must not recreate the cache.
      settlePendingWrite()

      const activeWrite = inFlight
      removalPromise = (async () => {
        if (activeWrite) await activeWrite
        await inner.removeClient()
      })().finally(() => {
        removalPromise = null
        lastWriteStartedAt = 0
        // Writes received while removal was in progress belong to the new cache.
        schedule()
      })

      return removalPromise
    },
  }
}
